import { useEffect, useState } from 'react';
import { Button, Form, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { faArrowRotateRight, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OpenOrdersTable from 'src/products/teleophthStandalone/pages/TeleophthStandaloneNewExam/OpenOrdersTable';

import useLocalStore from 'src/store/useLocalStore';

import CameraLanguageDropdown from 'src/components/custom_components/CameraLanguageDropdown';
import SitesDropdown from 'src/components/custom_components/SitesDropdown';
import NewPatientCreation from 'src/components/NewPatientCreation/NewPatientCreation';
import SearchPatientInfoFormGroup, {
  SearchPatientFormValues,
} from 'src/components/SearchPatientInfoFormGroup/SearchPatientInfoFormGroup';

import { I18NextNS } from 'src/models/Language.model';

import useCheckCameraConnection from 'src/hooks/useCheckCameraConnection';
import useGetExistingSiteData from 'src/hooks/useGetExistingSiteData';
import { useGetFilterOrder } from 'src/hooks/useGetFilterOrder';

export default function TeleophthStandaloneNewExam() {
  const { isCameraConnected } = useCheckCameraConnection();
  const { t } = useTranslation(I18NextNS.PAGES);
  const { isLoadingSites, existingSites, getExistingSiteData } =
    useGetExistingSiteData();
  const { getFilteredOrders, ordersData, isGettingOrders } =
    useGetFilterOrder();
  const { selectedSiteId } = useLocalStore();

  const [isNewPatientModalOpen, setIsNewPatientModalOpen] = useState(false);
  const [searchValues, setSearchValues] = useState<SearchPatientFormValues>();
  const [currentPage, setCurrentPage] = useState(1);

  const resetSearchValues = () => {
    setSearchValues(undefined);
    getFilteredOrders({ siteId: selectedSiteId, pageNumber: 0 });
    setCurrentPage(1);
  };

  useEffect(() => {
    getExistingSiteData();
  }, [getExistingSiteData]);

  useEffect(() => {
    getFilteredOrders({ siteId: selectedSiteId, pageNumber: 0, searchValues });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Stack gap={3} className="h-100">
        <div className="d-flex flex-column flex-md-row justify-content-md-between gap-3">
          <div className="flex-grow-1">
            <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
              <div>
                <h3 style={{ whiteSpace: 'nowrap' }}>{t('newExam.desc')}</h3>
              </div>
              <Button
                variant="primary-100"
                className="d-flex align-items-center"
                onClick={() => setIsNewPatientModalOpen(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                <span style={{ whiteSpace: 'nowrap' }}>
                  {t('newExam.createNewOrder')}
                </span>
              </Button>
            </div>
            <div className="text-primary d-flex flex-wrap gap-2 align-items-center mt-2">
              <FontAwesomeIcon icon={faArrowRotateRight} />
              {t('newExam.resume')}
            </div>
          </div>
          {isCameraConnected && (
            <div className="flex-shrink-0">
              <CameraLanguageDropdown />
            </div>
          )}
        </div>
        <div
          className="flex-grow-1 d-flex flex-column"
          style={{ minHeight: 0 }}
        >
          <div
            className="flex-grow-1 d-flex flex-column"
            style={{ minHeight: 0 }}
          >
            <Stack direction="horizontal" gap={3} className="mb-3">
              <div className="h-100" style={{ paddingTop: '2px' }}>
                <Form.Label>
                  {t('sites.title', { ns: I18NextNS.COMMON })}
                </Form.Label>
                <SitesDropdown
                  toggleClassName="py-2"
                  isLoadingSites={isLoadingSites}
                  existingSites={existingSites}
                  handleSiteChange={(siteId) => {
                    getFilteredOrders({ siteId, pageNumber: 0, searchValues });
                    setCurrentPage(1);
                  }}
                />
              </div>
              <SearchPatientInfoFormGroup
                onSearchValuesChange={(searchValues) => {
                  setSearchValues(searchValues);
                  getFilteredOrders({
                    siteId: selectedSiteId,
                    pageNumber: 0,
                    searchValues,
                  });
                  setCurrentPage(1);
                }}
                onFormReset={resetSearchValues}
                values={searchValues}
              />
            </Stack>
            <div
              className="flex-grow-1 d-flex flex-column"
              style={{ minHeight: 0 }}
            >
              <div className="flex-grow-1" style={{ minHeight: 0 }}>
                <OpenOrdersTable
                  ordersData={ordersData}
                  isLoading={isGettingOrders}
                  onFetchOrders={(pageNumber) =>
                    getFilteredOrders({
                      siteId: selectedSiteId,
                      pageNumber,
                      searchValues,
                    })
                  }
                  onPageChange={setCurrentPage}
                  currentPage={currentPage}
                />
              </div>
            </div>
          </div>
        </div>
      </Stack>
      {isNewPatientModalOpen && (
        <NewPatientCreation
          isOpen={isNewPatientModalOpen}
          onClose={() => {
            setIsNewPatientModalOpen(false);
            resetSearchValues();
          }}
          initialValues={searchValues}
        />
      )}
    </>
  );
}
